import { useTranslation } from 'react-i18next'
import { formatDistanceToNow } from 'date-fns'
import { enUS, sv } from 'date-fns/locale'
import { Bell } from 'lucide-react'
import type { Notification } from '@/lib/types'
import { cn } from '@/lib/utils'

const dot: Record<string, string> = {
  success: 'bg-status-opportunity',
  warning: 'bg-window',
  error: 'bg-destructive',
  info: 'bg-primary',
}

export function NotificationList({ items }: { items: Notification[] }) {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage === 'sv' ? sv : enUS

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center px-6 py-10 text-center">
        <Bell className="mb-3 size-5 text-muted-foreground" />
        <p className="text-sm font-medium">{t('notifications.emptyTitle')}</p>
        <p className="mt-1 max-w-56 text-sm text-muted-foreground">{t('notifications.emptyDescription')}</p>
      </div>
    )
  }

  return (
    <ul className="divide-y">
      {items.map((n) => {
        let when = t('notifications.unknownTime')
        try {
          when = formatDistanceToNow(new Date(n.created_at), { addSuffix: true, locale })
        } catch {
          /* keep fallback */
        }
        return (
          <li key={n.id} className="flex gap-3 px-4 py-3">
            <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', dot[n.type] ?? 'bg-muted-foreground')} aria-hidden />
            <div className="min-w-0 flex-1">
              <p className={cn('text-sm leading-snug', !n.read && 'font-medium')}>{n.message}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {when}
                {!n.read && <span className="sr-only">, {t('notifications.new')}</span>}
              </p>
            </div>
          </li>
        )
      })}
    </ul>
  )
}
